import { brand } from "../../theme";
import {
  Box,
  Typography,
  Container,
  Button,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { Link } from "react-router-dom";
import DrAnneNoBackground from "../../assets/images/profile/drannenobackground.webp";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";

const Welcome = () => {
  const isIpadPro = useMediaQuery("(min-width:1000px) and (max-width:1366px)");

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 4, sm: 6, md: 8 },
        pb: { xs: 12, sm: 14, md: 16, lg: 20 }, // Increased bottom padding
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          background:
            "linear-gradient(160deg, rgba(255,255,255,0) 0%, rgba(251,241,239,0.3) 50%, rgba(251,241,239,0.5) 100%)",
          zIndex: 0,
        }}
      />

      {/* Bottom fade transition to next section */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "35%",
          background:
            "linear-gradient(0deg, rgba(251,241,239,0.9) 0%, rgba(251,241,239,0) 100%)",
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          right: { xs: "-30%", md: "-15%" },
          bottom: { xs: "5%", md: "15%" },
          width: { xs: "500px", md: "600px" },
          height: { xs: "500px", md: "600px" },
          borderRadius: "50%",
          backgroundColor: alpha(brand.peach, 0.03),
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          left: { xs: "-20%", md: "-5%" },
          top: { xs: "10%", md: "15%" },
          width: { xs: "300px", md: "400px" },
          height: { xs: "300px", md: "400px" },
          borderRadius: "50%",
          backgroundColor: alpha(brand.accent, 0.05),
          zIndex: 0,
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 2, sm: 3, md: 4, lg: 6 }, // Added responsive horizontal padding
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 6, md: 4, lg: 8 },
          }}
        >
          {/* Text Content Column */}
          <Box
            sx={{
              width: { xs: "100%", md: "auto", lg: "60%" },
              flex: { md: "1 1 0", lg: "0 0 auto" },
              minWidth: 0,
              textAlign: { xs: "center", md: "left" },
              order: { xs: 2, md: 1 },
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: { xs: "-60px", md: "-70px" },
                  left: { xs: "50%", md: "-20px" },
                  transform: { xs: "translateX(-50%)", md: "translateX(0)" },
                  width: { xs: "150px", md: "180px" },
                  height: { xs: "150px", md: "180px" },
                  borderRadius: "50%",
                  backgroundColor: alpha(brand.peach, 0.1),
                  zIndex: -1,
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  color: "headline.main",
                  mb: { xs: 1, md: 0 },
                  position: "relative",
                }}
              >
                Board Certified
              </Typography>

              <Typography
                component="h2"
                variant="h1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  color: "headline.main",
                  position: "relative",
                }}
              >
                Clinical Sexologist
              </Typography>
            </Box>

            <Typography
              variant="subtitle1"
              sx={{
                mt: 4,
                mb: 5,
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                color: "text.secondary",
                maxWidth: { xs: "100%", md: "90%" },
                mx: { xs: "auto", md: 0 },
                lineHeight: 1.6,
              }}
            >
              Whatever brought you here, it's probably not easy to talk about.
              Dr. Rothenberg has spent her career helping people work through
              exactly these kinds of concerns, with warmth and without judgment.
            </Typography>

            {/* Call to action buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: "wrap",
                gap: { xs: 2, sm: 2, md: isIpadPro ? 1.5 : 2 }, // Adjusted gap for iPad Pro
                justifyContent: { xs: "center", md: "flex-start" },
                mb: { xs: 4, md: 0 }, // Added bottom margin on mobile
              }}
            >
              <Button
                component={Link}
                to="/request-consultation"
                variant="contained"
                startIcon={<ForumOutlinedIcon />}
                sx={{
                  backgroundColor: "secondary.main",
                  color: "text.primary",
                  fontSize: {
                    xs: "0.9rem",
                    md: isIpadPro ? "0.85rem" : "1rem",
                  }, // Smaller on iPad Pro
                  fontWeight: 600,
                  py: { xs: 1.5, md: isIpadPro ? 1.25 : 1.5 }, // Adjusted for iPad Pro
                  px: { xs: 3, md: isIpadPro ? 2 : 3 }, // Adjusted for iPad Pro
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "0 4px 20px rgba(228, 181, 162, 0.25)",
                  minWidth: { xs: "100%", sm: "auto" },
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: "peach.main",
                    boxShadow: "0 6px 25px rgba(228, 181, 162, 0.4)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Request a Consultation
              </Button>

              <Button
                component="a"
                href="tel:407-797-5469"
                variant="outlined"
                startIcon={<PhoneOutlinedIcon />}
                sx={{
                  border: `2px solid ${alpha(brand.accent, 0.8)}`,
                  color: "#444",
                  fontSize: {
                    xs: "0.9rem",
                    md: isIpadPro ? "0.85rem" : "1rem",
                  }, // Smaller on iPad Pro
                  fontWeight: 600,
                  py: { xs: 1.4, md: isIpadPro ? 1.15 : 1.4 }, // Adjusted for iPad Pro
                  px: { xs: 3, md: isIpadPro ? 2 : 3 }, // Adjusted for iPad Pro
                  borderRadius: "8px",
                  textTransform: "none",
                  minWidth: { xs: "100%", sm: "auto" },
                  minHeight: 44,
                  whiteSpace: "nowrap",
                  transition:
                    "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    border: `2px solid ${brand.peach}`,
                    backgroundColor: alpha(brand.peach, 0.05),
                  },
                }}
              >
                Call me Directly
              </Button>

              <Button
                component="a"
                href="sms:407-797-5469?body=Hello%20Dr.%20Anne%20Rothenberg,%20I%27d%20like%20to%20inquire%20about..."
                variant="outlined"
                startIcon={<MessageOutlinedIcon />}
                sx={{
                  border: `2px solid ${alpha(brand.accent, 0.8)}`,
                  color: "#444",
                  fontSize: {
                    xs: "0.9rem",
                    md: isIpadPro ? "0.85rem" : "1rem",
                  }, // Smaller on iPad Pro
                  fontWeight: 600,
                  py: { xs: 1.4, md: isIpadPro ? 1.15 : 1.4 }, // Adjusted for iPad Pro
                  px: { xs: 3, md: isIpadPro ? 2 : 3 }, // Adjusted for iPad Pro
                  borderRadius: "8px",
                  textTransform: "none",
                  minWidth: { xs: "100%", sm: "auto" },
                  minHeight: 44,
                  whiteSpace: "nowrap",
                  transition:
                    "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    border: `2px solid ${brand.peach}`,
                    backgroundColor: alpha(brand.peach, 0.05),
                  },
                }}
              >
                Text me Directly
              </Button>
            </Box>
          </Box>

          {/* Image Column */}
          <Box
            sx={{
              width: { xs: "100%", md: "auto", lg: "40%" },
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              order: { xs: 1, md: 2 },
              position: "relative",
              mt: { xs: 2, md: 0 }, // Added top margin on mobile
              mb: { xs: 2, md: 0 }, // Added bottom margin on mobile
            }}
          >
            {/* Main image with background */}
            <Box
              sx={{
                position: "relative",
                width: { xs: 280, sm: 320, md: isIpadPro ? 300 : 380 }, // Adjusted for iPad Pro
                height: { xs: 280, sm: 320, md: isIpadPro ? 300 : 380 }, // Adjusted for iPad Pro
              }}
            >
              {/* Background gradient circle */}
              <Box
                sx={{
                  position: "absolute",
                  width: "90%",
                  height: "90%",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${brand.peach} 0%, ${brand.accent} 100%)`,
                  top: "5%",
                  left: "5%",
                  zIndex: 0,
                  boxShadow: "0 10px 30px rgba(228, 181, 162, 0.3)",
                }}
              />

              {/* Circle border */}
              <Box
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  border: "3px solid rgba(255, 255, 255, 0.8)",
                  boxSizing: "border-box",
                  top: 0,
                  left: 0,
                  zIndex: 2,
                }}
              />

              {/* Image */}
              <Box
                component="img"
                src={DrAnneNoBackground}
                alt="Dr. Rothenberg"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                width={608}
                height={666}
                sx={{
                  width: "80%",
                  height: "auto",
                  borderRadius: "50%",
                  position: "absolute",
                  top: "10%",
                  left: "10%",
                  zIndex: 1,
                  filter: "drop-shadow(0 5px 15px rgba(0,0,0,0.1))",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Welcome;
